/**
 * Goethe-Zertifikat B2 blog post: Redemittel Schreiben & Sprechen (PDF).
 * Content: intro, Goethe B2 speaking, mock exam, practising under exam conditions, FAQ.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import RedemittelPhraseTables from './RedemittelPhraseTables';

const BLOG_FAQ_ITEMS = [
  {
    question: 'Where can I access the redemittel b2 pdf?',
    answer:
      'On this page. The download link above gives you the complete Goethe B2 list as a free PDF, with Schreiben and Sprechen phrases, English meanings and example sentences. No account is needed.',
  },
  {
    question: 'Does Logami have Goethe B2 mock exams?',
    answer:
      'Yes. There are 12 complete Goethe B2 mock exams in the official format, covering Lesen, Hören, Schreiben and Sprechen, with AI feedback on your written answers. Your first mock exam is free.',
  },
];

function BlogFaqItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen
            ? 'border-black shadow-2xl scale-[1.02]'
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        aria-expanded={isOpen}
      >
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen
                ? 'bg-black text-white scale-110 rotate-6'
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              <HelpCircle className="w-6 h-6 relative z-10" />
            </div>
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>
          <div className="flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen ? 'rotate-180 text-black scale-125' : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'}
            `} />
          </div>
        </div>
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>
            <p className="text-black/70 leading-relaxed text-base md:text-lg max-w-none">
              {answer}
            </p>
          </div>
        </div>
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl pointer-events-none" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>
    </div>
  );
}

const GoetheB2RedemittelPost = ({ pdfUrl }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        Redemittel are the fixed phrases that carry an exam answer: how you state a position, weigh arguments or write a formal message. In the <strong>Goethe B2</strong> exam they count in <strong>Schreiben</strong>, two tasks in 75 minutes (a forum post giving your opinion and a formal message), and in <strong>Sprechen</strong>, about 15 minutes with a presentation and a discussion. Below are the <strong>redemittel b2</strong> sorted by task, with English meanings and examples, and the full list is a free PDF.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How Do You Apply Goethe B2 Speaking Redemittel?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        To succeed in the <strong>goethe b2 speaking</strong> module, candidates must deploy specific transitional phrases to connect complex arguments. Utilizing these formal connectors demonstrates advanced syntactic control to the examiners, ensuring you secure maximum points for structural coherence and vocabulary precision.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        The updated <strong>b2 neu exam</strong> demands rigorous presentation structuring. Instead of struggling with basic conjunctions under a strict time limit, test takers must integrate advanced <strong>redemittel b2 neu</strong> to argue, hypothesize, and conclude effectively. Having a reliable <strong>b2 pdf guide</strong> ensures you memorize the exact phrases needed to present your Thema (topic) and actively debate with your partner.
      </p>


      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Executing the Redemittel Mock Examination
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Executing a <strong>redemittel mock examination</strong> requires immediate, spontaneous language production under severe time constraints. Memorizing targeted <strong>german b2</strong> phrases allows test-takers to confidently introduce topics, express personal opinions, and lead debates without hesitation during the conversational segment.
      </p>


      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Practising the Phrases Under Exam Conditions
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        A phrase list helps most when you can recall it under time pressure. Learn the phrases from the tables below
        or the free PDF, then use them in a timed answer. Logami is built by German teachers and has 12 complete Goethe B2 mock exams
        in the official format, gives AI feedback on your written answers, and includes a Redemittel trainer that
        brings each phrase back just before you are likely to forget it. Your first mock exam is free.
      </p>

      {pdfUrl && (
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
          For the complete B2 Redemittel collection.{' '}
          <a
            href={pdfUrl}
            download={pdfUrl.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
          >
            Download the full Goethe B2 Redemittel PDF here
          </a>
          .
        </p>
      )}

      <RedemittelPhraseTables
        provider="goethe"
        level="b2"
        examName="Goethe B2"
        pdfUrl={pdfUrl}
      />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-12 mb-6">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {BLOG_FAQ_ITEMS.map((item, index) => (
          <BlogFaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openFaqIndex === index}
            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
          />
        ))}
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        <strong>Last Updated:</strong> October 7, 2026
      </p>
      <SprechenSamplesCallout provider="goethe" level="b2" />
      <RelatedResources provider="goethe" level="b2" variant="redemittel" />

    </article>
  );
};

export default GoetheB2RedemittelPost;
